import type { ColumnDef } from '@tanstack/react-table';

import { type GetStockTransfersQuery, ReassortmentFlow, StockTransferState } from 'data/__generated__';
import { textColumn, dateRangeColumn, numberColumn, multiSelectColumn, booleanColumn } from 'components/DataTable';
import { formatDateForDisplay } from 'helpers/dateTimes';

import { StatusTag } from '../components/StatusTag';

export type StockTransferRow = GetStockTransfersQuery['stockTransfers']['stockTransfers'][number];

type DateRangeFilter = { gte?: number; lte?: number };

export type StockTransfersFilter = {
    searchValue?: string;
    states?: StockTransferState[] | null;
    createdAt?: DateRangeFilter;
    updatedAt?: DateRangeFilter;
    transferDate?: DateRangeFilter;
    originColumnName?: string;
    destinationColumnName?: string;
    reassortmentFlow?: ReassortmentFlow[] | null;
    isForReassortment?: boolean;
    shouldUpdateStockImmediately?: boolean;
};

export const stockTransfersColumns: ColumnDef<StockTransferRow, unknown>[] = [
    dateRangeColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'transferDate',
        header: 'Date de transfert',
        filterKey: 'transferDate',
        defaultVisible: true,
        cell: ({ row }) => <span>{formatDateForDisplay(row.original.transferDate)}</span>,
    }),
    multiSelectColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'states',
        defaultVisible: true,
        options: Object.values(StockTransferState).map((state) => ({ value: state, label: state })),
        renderOption: (option) => <StatusTag state={option.value as StockTransferState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    textColumn<StockTransferRow, StockTransfersFilter>({
        id: 'originColumn',
        accessorFn: (row) => row.originColumn.fullname,
        header: 'Colonne de départ',
        filterKey: 'originColumnName',
        placeholder: 'Nom de colonne',
        defaultVisible: true,
    }),
    textColumn<StockTransferRow, StockTransfersFilter>({
        id: 'destinationColumn',
        accessorFn: (row) => row.destinationColumn.fullname,
        header: "Colonne d'arrivée",
        filterKey: 'destinationColumnName',
        placeholder: 'Nom de colonne',
        defaultVisible: true,
    }),
    numberColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'numberOfRefs',
        header: 'Nombre de références',
        defaultVisible: true,
    }),
    numberColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'quantityOfProducts',
        header: 'Nombre de produits',
        defaultVisible: true,
    }),
    multiSelectColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'reassortmentFlow',
        header: 'Flux de réassort',
        filterKey: 'reassortmentFlow',
        defaultVisible: false,
        options: Object.values(ReassortmentFlow).map((flow) => ({ value: flow, label: flow })),
        cell: ({ getValue }) => <span>{(getValue() as ReassortmentFlow | null) ?? '—'}</span>,
    }),
    booleanColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'isForReassortment',
        header: 'Pour réassort',
        filterKey: 'isForReassortment',
        defaultVisible: false,
    }),
    booleanColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'shouldUpdateStockImmediately',
        header: 'MAJ stock immédiate',
        filterKey: 'shouldUpdateStockImmediately',
        defaultVisible: false,
    }),
    dateRangeColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'createdAt',
        header: 'Date de création',
        filterKey: 'createdAt',
        defaultVisible: false,
    }),
    dateRangeColumn<StockTransferRow, StockTransfersFilter>({
        accessorKey: 'updatedAt',
        header: 'Dernière modif.',
        filterKey: 'updatedAt',
        defaultVisible: true,
    }),
];
